<template>
  <div data-testid="mcp-how-it-works">
    <section class="zone-card">
      <div class="zone-facts">
        <div class="zone-fact">
          <i class="material-symbols-rounded">lock</i>
          <div>
            <b>Read-only</b>
            <p>AI apps cannot create, change, or delete anything in Hubstaff.</p>
          </div>
        </div>

        <div class="zone-fact">
          <i class="material-symbols-rounded">person</i>
          <div>
            <b>Permission-aware</b>
            <p>Each person only sees the data their Hubstaff role already allows.</p>
          </div>
        </div>

        <div class="zone-fact">
          <i class="material-symbols-rounded">shield</i>
          <div>
            <b>Secure sign-in</b>
            <p>You sign in through Hubstaff. Your password is never shared with the AI app.</p>
          </div>
        </div>

        <div class="zone-fact">
          <i class="material-symbols-rounded">groups</i>
          <div>
            <b>For the whole team</b>
            <p>
              Everyone can connect, including owners, managers, team leads, and users, so each role can get the info
              they need to do their jobs better.
            </p>
          </div>
        </div>
      </div>

      <div class="zone-card-divider" />

      <div class="zone-btn-row">
        <button
          type="button"
          class="zbtn zbtn-outline zbtn-sm"
          data-testid="mcp-open-details"
          @click="showDetails = true"
        >
          What AI apps can access
        </button>

        <a
          :href="developerPortalUrl"
          class="zbtn zbtn-outline zbtn-sm"
          target="_blank"
          rel="noopener"
          data-testid="mcp-developer-portal-link"
        >
          Setup guide
          <i class="material-symbols-rounded">launch</i>
        </a>
      </div>

    </section>

    <AppDialog
      v-if="showDetails"
      :show="showDetails"
      append-to-body
      title="What AI apps can access"
      data-testid="mcp-details-dialog"
      @close="showDetails = false"
    >
      <div class="mcp-detail-scroll">
        <p class="mcp-detail-lead">
          Hubstaff MCP lets an AI app answer questions using supported Hubstaff data the signed-in person can
          already view.
        </p>

        <div class="mcp-detail-row">
          <i class="material-symbols-rounded">lock</i>
          <div>
            <b>Read-only access</b>
            <p>
              AI apps can read the same Hubstaff data you can: organizations, team members, projects and tasks,
              time and activity, and more. They cannot create, change, or delete anything in Hubstaff.
            </p>
          </div>
        </div>

        <div class="mcp-detail-row">
          <i class="material-symbols-rounded">person</i>
          <div>
            <b>Permission-aware</b>
            <p>
              Each person only receives information their Hubstaff role and existing permissions already allow
              them to see. Restricted data stays restricted.
            </p>
          </div>
        </div>

        <div class="mcp-detail-row">
          <i class="material-symbols-rounded">shield</i>
          <div>
            <b>Secure sign-in</b>
            <p>
              Every person signs in through Hubstaff and approves their own connection. Hubstaff passwords and
              access keys are never shared with the AI app.
            </p>
          </div>
        </div>
      </div>

      <template #footer>
        <AppButton
          type="primary"
          @click="showDetails = false"
        >
          Done
        </AppButton>
      </template>
    </AppDialog>
  </div>
</template>

<script>
export default {
  name: 'McpHowItWorks',
  props: {
    developerPortalUrl: { type: String, required: true },
  },
  data() {
    return { showDetails: false };
  },
};
</script>

<style scoped>
.zone-card {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
  background: #fff;
  border: 1px solid var(--gray-200);
  border-radius: var(--rounded-lg);
}

.zone-facts {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.zone-fact {
  display: flex;
  align-items: center;
  gap: 8px;
}

.zone-fact > .material-symbols-rounded {
  flex: none;
  font-size: 20px;
  line-height: 1;
  color: var(--gray-900);
}

.zone-fact b {
  display: block;
  color: var(--gray-900);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.zone-fact p {
  margin: 0;
  color: var(--gray-500);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.zone-card-divider {
  height: 1px;
  background: var(--gray-200);
}

.zone-btn-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.zbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-height: 40px;
  padding: 10px 20px;
  border: 1px solid transparent;
  border-radius: var(--rounded-md);
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
}

.zbtn .material-symbols-rounded {
  font-size: 18px;
  line-height: 16px;
}

.zbtn-outline {
  border-color: var(--primary-500);
  color: var(--primary-500);
  background: #fff;
}

.zbtn-outline:hover {
  background: var(--primary-50);
  color: var(--primary-500);
}

.zbtn-sm {
  max-height: 32px;
  padding: 8px 12px;
}

.mcp-detail-scroll {
  max-height: 60vh;
  overflow-y: auto;
}

.mcp-detail-lead {
  margin: 0 0 16px;
  color: var(--gray-500);
  font-size: 14px;
  line-height: 20px;
}

.mcp-detail-row {
  display: flex;
  gap: 12px;
}

.mcp-detail-row + .mcp-detail-row {
  margin-top: 16px;
}

.mcp-detail-row > .material-symbols-rounded {
  flex: none;
  font-size: 20px;
  color: var(--gray-900);
}

.mcp-detail-row b {
  display: block;
  color: var(--gray-900);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.mcp-detail-row p {
  margin: 0;
  color: var(--gray-500);
  font-size: 14px;
  line-height: 20px;
}
</style>
